// src/components/admin/CommunicationsAI.jsx
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { CommunicationsAIService } from '../../api/backendAiService';
import DailyTokenCounter from './DailyTokenCounter';
import { storage } from '../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// HTML Email Preview Modal
function HtmlEmailPreviewModal({ isOpen, onClose, html }) {
  if (!isOpen || !html) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff', borderRadius: 8, width: '90%', maxWidth: 680,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', borderBottom: '1px solid #e0e0e0', background: '#f8f8f8'
        }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>HTML Email Preview</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 20, color: '#666', lineHeight: 1
            }}
          >×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          <iframe
            title="Email Preview"
            sandbox="allow-same-origin"
            srcDoc={html}
            style={{
              width: '100%', minHeight: 500, border: '1px solid #ddd',
              borderRadius: 4, background: '#fff'
            }}
          />
        </div>
      </div>
    </div>
  );
}

function CommunicationsAI({ salonId, onUseInEmailEditor, onUseInWhatsAppEditor, onUseInPushEditor }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [salonContext, setSalonContext] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { url, name, localPreview }
  const [imageUploading, setImageUploading] = useState(false);

  const [limitReached, setLimitReached] = useState(false);
  const chatMessagesRef = useRef(null);
  const recognitionRef = useRef(null);
  const aiServiceRef = useRef(null);
  const fileInputRef = useRef(null);
  const tokenCounterRef = useRef(null);

  // Initialize AI service
  useEffect(() => {
    if (salonId && !aiServiceRef.current) {
      aiServiceRef.current = new CommunicationsAIService(salonId);
    }
  }, [salonId]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  // Load salon context
  useEffect(() => {
    const loadContext = async () => {
      if (!aiServiceRef.current) return;

      try {
        const context = await aiServiceRef.current.getSalonContext();
        setSalonContext(context);

        const initialMessage = {
          id: Date.now(),
          type: 'ai',
          content: `Hello! I'm your Bespoke AI assistant for communications and campaigns. I can help you create compelling email campaigns and push notifications that engage your customers.

I have access to your salon's data:
• ${context.customers?.length || 0} customer profiles
• ${context.pastCampaigns?.length || 0} past campaigns
• ${context.services?.length || 0} services

How can I help you with your communications today? I can write subject lines, email copy, push notifications, suggest campaign strategies, or **design a full branded HTML email template** ready to send.`,
          timestamp: new Date()
        };

        setMessages([initialMessage]);
      } catch (error) {
        console.error('Error loading salon context:', error);

        const errorMessage = {
          id: Date.now(),
          type: 'system',
          content: 'I had trouble loading your salon data. I can still help you create communications. What would you like to create?',
          timestamp: new Date()
        };
        setMessages([errorMessage]);
      }
    };

    if (salonId && aiServiceRef.current) {
      loadContext();
    }
  }, [salonId]);

  // Track previous message count to detect user-triggered additions (not initial load)
  const prevMessagesLengthRef = useRef(0);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-scroll chat container (not window) to latest message, skipping initial welcome msg
  useEffect(() => {
    const prev = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;
    // Only scroll when messages grow beyond the initial welcome message (i.e. user has interacted)
    if (messages.length > prev && prev > 0 && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle voice input
  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Handle image file selection and upload to Firebase Storage
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setImageUploading(true);

    try {
      const storageRef = ref(storage, `campaign-images/${salonId}/${Date.now()}-${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setPendingImage({ url, name: file.name, localPreview });
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Image upload failed. Please try again.');
    } finally {
      setImageUploading(false);
      // Reset input so same file can be re-selected if needed
      e.target.value = '';
    }
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && !pendingImage) || isLoading || !aiServiceRef.current || limitReached) return;

    // Build the message text — append image info if one is attached
    let messageText = inputMessage.trim();
    if (pendingImage) {
      const imageLine = `\n\n[UPLOADED IMAGE: ${pendingImage.name} | URL: ${pendingImage.url}]`;
      messageText = messageText + imageLine;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim() || '(Image attached)',
      imagePreview: pendingImage ? pendingImage.localPreview : null,
      imageName: pendingImage ? pendingImage.name : null,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setPendingImage(null);
    setIsLoading(true);

    try {
      const response = await aiServiceRef.current.sendMessage(messageText, salonContext);

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.message,
        htmlEmail: response.htmlEmail || null,
        textEmail: response.textEmail || null,
        emailSubject: response.emailSubject || null,
        whatsappMessage: response.whatsappMessage || null,
        whatsappImage: response.whatsappImage || null,
        pushTitle: response.pushTitle || null,
        pushBody: response.pushBody || null,
        pushImage: response.pushImage || null,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      setShowFeedback(prev => ({ ...prev, [aiMessage.id]: true }));
      if (response.dailyUsage) tokenCounterRef.current?.update(response.dailyUsage);

    } catch (error) {
      console.error('Error sending message:', error);
      const isLimitError = error?.status === 429 || error?.message?.includes('daily AI usage limit');
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: isLimitError
          ? 'You have exceeded your daily AI usage limit of 10,000 tokens. AI is unavailable until 00:00:01.'
          : "I'm experiencing technical difficulties. Please try again or contact support if the issue persists.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  // Handle feedback
  const handleFeedback = async (messageId, feedback) => {
    try {
      await aiServiceRef.current.storeFeedback(messageId, feedback);
      setShowFeedback(prev => ({ ...prev, [messageId]: false }));

      const confirmationMessage = {
        id: Date.now(),
        type: 'system',
        content: `Thank you for your feedback! This helps me improve my recommendations.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmationMessage]);
    } catch (error) {
      console.error('Error storing feedback:', error);
    }
  };

  // Handle example prompt click
  const handleExampleClick = (prompt) => {
    setInputMessage(prompt);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleOpenPreview = (html) => {
    setPreviewHtml(html);
    setShowPreviewModal(true);
  };

  const handleUseInEmailEditorHtml = (html, subject) => {
    if (onUseInEmailEditor) onUseInEmailEditor(html, subject, 'html');
  };

  const handleUseInEmailEditorText = (text, subject) => {
    if (onUseInEmailEditor) onUseInEmailEditor(text, subject, 'text');
  };

  const handleUseInWhatsApp = (message) => {
    if (onUseInWhatsAppEditor) onUseInWhatsAppEditor(message);
  };

  const handleUseInPush = (title, body) => {
    if (onUseInPushEditor) onUseInPushEditor(title, body);
  };

  return (
    <div className="ai-tab-container">
      <HtmlEmailPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        html={previewHtml}
      />

      <DailyTokenCounter
        ref={tokenCounterRef}
        salonId={salonId}
        onLimitReached={setLimitReached}
      />

      <div className="ai-tab-header">
        <h2>Communications AI</h2>
        <p>Get AI-powered assistance for creating engaging email campaigns and push notifications</p>
      </div>

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try asking me:</h3>
          <div className="example-prompts">
            <div
              className="prompt-card"
              onClick={() => handleExampleClick("Write an email subject line to promote our new services")}
            >
              "Write an email subject line to promote our new services"
            </div>
            <div
              className="prompt-card"
              onClick={() => handleExampleClick("Create a push notification to remind customers about appointment confirmations")}
            >
              "Create a push notification to remind customers about appointment confirmations"
            </div>
            <div
              className="prompt-card"
              onClick={() => handleExampleClick("Design a Christmas HTML email using my salon's branding to announce 30% off all services from 1–31 December")}
            >
              "Design a Christmas HTML email using my salon's branding to announce 30% off all services from 1–31 December"
            </div>
            <div
              className="prompt-card"
              onClick={() => handleExampleClick("Create a branded HTML email for our New Year promotion with a 20% discount")}
            >
              "Create a branded HTML email for our New Year promotion with a 20% discount"
            </div>
          </div>
        </div>

        <div className="ai-chat-interface">
          <div className="chat-messages" ref={chatMessagesRef}>
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}-message`}>
                {message.type === 'ai' && (
                  <div className="ai-avatar">🤖</div>
                )}
                {message.type === 'user' && (
                  <div className="user-avatar">👤</div>
                )}
                {message.type === 'system' && (
                  <div className="system-avatar">ℹ️</div>
                )}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{message.content}</ReactMarkdown></div>

                  {/* Attached image thumbnail in user message */}
                  {message.imagePreview && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={message.imagePreview}
                        alt={message.imageName || 'Attached image'}
                        style={{
                          maxWidth: 180, maxHeight: 120, borderRadius: 6,
                          border: '1px solid #ddd', display: 'block',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        📎 {message.imageName}
                      </div>
                    </div>
                  )}

                  {/* HTML Email Card */}
                  {message.htmlEmail && (
                    <div style={{
                      marginTop: 12,
                      border: '1px solid #d0e4f7',
                      borderRadius: 8,
                      background: '#f0f7ff',
                      padding: 14
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 18 }}>📧</span>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#1a5c9e' }}>
                          HTML Email Template Generated
                        </span>
                      </div>
                      {message.emailSubject && (
                        <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
                          <strong>Suggested subject:</strong> {message.emailSubject}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleOpenPreview(message.htmlEmail)}
                          style={{
                            padding: '7px 14px', borderRadius: 6,
                            border: '1px solid #1a5c9e', background: '#fff',
                            color: '#1a5c9e', cursor: 'pointer', fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          Preview Email
                        </button>
                        {onUseInEmailEditor && (
                          <button
                            onClick={() => handleUseInEmailEditorHtml(message.htmlEmail, message.emailSubject)}
                            style={{
                              padding: '7px 14px', borderRadius: 6,
                              border: 'none', background: '#1a5c9e',
                              color: '#fff', cursor: 'pointer', fontSize: 13,
                              fontWeight: 500
                            }}
                          >
                            Use in Email Editor
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Text Email Card */}
                  {message.textEmail && (
                    <div style={{
                      marginTop: 12,
                      border: '1px solid #c8e6c9',
                      borderRadius: 8,
                      background: '#f1f8e9',
                      padding: 14
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 18 }}>✉️</span>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#2e7d32' }}>
                          Text Email Generated
                        </span>
                      </div>
                      {message.emailSubject && (
                        <div style={{ fontSize: 13, color: '#444', marginBottom: 6 }}>
                          <strong>Subject:</strong> {message.emailSubject}
                        </div>
                      )}
                      <div style={{
                        fontSize: 13, color: '#333', background: '#fff',
                        border: '1px solid #dcedc8', borderRadius: 6,
                        padding: '8px 10px', marginBottom: 10,
                        whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto'
                      }}>
                        {message.textEmail}
                      </div>
                      {onUseInEmailEditor && (
                        <button
                          onClick={() => handleUseInEmailEditorText(message.textEmail, message.emailSubject)}
                          style={{
                            padding: '7px 14px', borderRadius: 6,
                            border: 'none', background: '#388e3c',
                            color: '#fff', cursor: 'pointer', fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          Use in Email Editor
                        </button>
                      )}
                    </div>
                  )}

                  {/* WhatsApp Message Card */}
                  {message.whatsappMessage && (
                    <div style={{
                      marginTop: 12,
                      border: '1px solid #b2dfdb',
                      borderRadius: 8,
                      background: '#e8f5e9',
                      padding: 14
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 18 }}>💬</span>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#00695c' }}>
                          WhatsApp Message Generated
                        </span>
                      </div>
                      {message.whatsappImage && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 12, color: '#555', marginBottom: 4, fontWeight: 500 }}>
                            📎 Media Image:
                          </div>
                          <img
                            src={message.whatsappImage}
                            alt="WhatsApp media"
                            style={{
                              maxWidth: '100%', maxHeight: 160, borderRadius: 6,
                              border: '1px solid #b2dfdb', display: 'block', objectFit: 'cover'
                            }}
                          />
                        </div>
                      )}
                      <div style={{
                        fontSize: 13, color: '#333', background: '#fff',
                        border: '1px solid #b2dfdb', borderRadius: 6,
                        padding: '8px 10px', marginBottom: 10,
                        whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto'
                      }}>
                        {message.whatsappMessage}
                      </div>
                      {onUseInWhatsAppEditor && (
                        <button
                          onClick={() => handleUseInWhatsApp(message.whatsappMessage)}
                          style={{
                            padding: '7px 14px', borderRadius: 6,
                            border: 'none', background: '#00796b',
                            color: '#fff', cursor: 'pointer', fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          Use in WhatsApp Editor
                        </button>
                      )}
                    </div>
                  )}

                  {/* Push Notification Card */}
                  {(message.pushTitle || message.pushBody) && (
                    <div style={{
                      marginTop: 12,
                      border: '1px solid #ffe0b2',
                      borderRadius: 8,
                      background: '#fff8e1',
                      padding: 14
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 18 }}>🔔</span>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#e65100' }}>
                          Push Notification Generated
                        </span>
                      </div>
                      {message.pushTitle && (
                        <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>
                          <strong>Title:</strong> {message.pushTitle}
                        </div>
                      )}
                      {message.pushBody && (
                        <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
                          <strong>Body:</strong> {message.pushBody}
                        </div>
                      )}
                      {message.pushImage && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 12, color: '#555', marginBottom: 4, fontWeight: 500 }}>
                            🖼️ Hero Image:
                          </div>
                          <img
                            src={message.pushImage}
                            alt="Push notification hero"
                            style={{
                              maxWidth: '100%', maxHeight: 140, borderRadius: 6,
                              border: '1px solid #ffe0b2', display: 'block', objectFit: 'cover'
                            }}
                          />
                        </div>
                      )}
                      {onUseInPushEditor && (
                        <button
                          onClick={() => handleUseInPush(message.pushTitle, message.pushBody)}
                          style={{
                            padding: '7px 14px', borderRadius: 6,
                            border: 'none', background: '#e65100',
                            color: '#fff', cursor: 'pointer', fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          Use in Push Notifications Editor
                        </button>
                      )}
                    </div>
                  )}

                  <div className="message-time">{formatTime(message.timestamp)}</div>

                  {message.type === 'ai' && showFeedback[message.id] && (
                    <div className="message-feedback">
                      <span>Was this helpful?</span>
                      <button
                        className="feedback-btn positive"
                        onClick={() => handleFeedback(message.id, 'positive')}
                      >
                        👍
                      </button>
                      <button
                        className="feedback-btn negative"
                        onClick={() => handleFeedback(message.id, 'negative')}
                      >
                        👎
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

          </div>

          <div className="chat-input-area">
            {/* Pending image preview */}
            {pendingImage && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', background: '#f0f7ff',
                border: '1px solid #c8dff7', borderRadius: 6, marginBottom: 8
              }}>
                <img
                  src={pendingImage.localPreview}
                  alt={pendingImage.name}
                  style={{
                    width: 52, height: 52, objectFit: 'cover',
                    borderRadius: 4, border: '1px solid #c8dff7', flexShrink: 0
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a5c9e', marginBottom: 2 }}>
                    📎 {pendingImage.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#666' }}>
                    Specify placement in your message (e.g. "use as hero image")
                  </div>
                </div>
                <button
                  onClick={() => setPendingImage(null)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 18, color: '#999', lineHeight: 1, flexShrink: 0
                  }}
                  title="Remove image"
                >×</button>
              </div>
            )}

            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at 00:00:01.' : 'Type your message here or click examples above... (Press Enter to send, Shift+Enter for new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />

            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />

            <div className="input-actions">
              <button
                className={`voice-input-btn ${isListening ? 'listening' : ''}`}
                onClick={handleVoiceInput}
                disabled={isLoading || limitReached}
              >
                {isListening ? '⏹️ Stop' : '🎤 Voice Input'}
              </button>
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={isLoading || imageUploading || limitReached}
                style={{
                  padding: '8px 14px', borderRadius: 6,
                  border: '1px solid #c0c0c0', background: '#fff',
                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  color: '#444', display: 'flex', alignItems: 'center', gap: 5
                }}
                title="Attach an image to include in the email, push notification, or WhatsApp message"
              >
                {imageUploading ? '⏳ Uploading...' : '📎 Attach Image'}
              </button>
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={isLoading || (!inputMessage.trim() && !pendingImage) || limitReached}
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ai-features-info">
        <h3>How to Use Communications AI:</h3>
        <div className="feature-list">
          <div className="feature-item">
            <strong>1. Describe Your Goal:</strong> Tell me what campaign you want to create
          </div>
          <div className="feature-item">
            <strong>2. Get Creative Suggestions:</strong> I'll provide copy, subject lines, and timing advice
          </div>
          <div className="feature-item">
            <strong>3. Generate HTML Emails:</strong> Ask me to "design" or "create" an HTML email and I'll build a branded template
          </div>
          <div className="feature-item">
            <strong>4. Attach Images:</strong> Click "📎 Attach Image" to upload a photo, then tell the AI where to place it (e.g. "use as the hero banner")
          </div>
          <div className="feature-item">
            <strong>5. Send to Email Editor:</strong> Click "Use in Email Editor" to load the template directly into the Email Campaigns tab
          </div>
        </div>

        <div className="ai-capabilities">
          <h4>AI Capabilities:</h4>
          <ul>
            <li>✅ Email subject line optimization</li>
            <li>✅ Email body copy writing</li>
            <li>✅ Branded HTML email template generation</li>
            <li>✅ Push notification creation</li>
            <li>✅ Personalization strategy</li>
            <li>✅ Send time recommendations</li>
            <li>✅ A/B testing suggestions</li>
            <li>✅ Campaign performance insights</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CommunicationsAI;
